import axios, { AxiosResponse } from 'axios';
import {
    ConveyorDrawingApplyPayload,
    ConveyorStationConfig,
    ConveyorStationWithCost,
    Equipment,
    ExtractedConveyorLayout,
    LaborRate,
    PredefinedStation,
    ProcessTemplate,
    ProcessTemplateCreate,
    ProcessTemplateUpdate,
    ProcurementLine,
    ProcurementLineUpdate,
    ProcurementResponse,
    RFQ,
    RFQCreate,
    RFQDocumentUploadResponse,
    RFQProgramParams,
    RFQUpdate,
    Vendor,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('rfq_access_token');
    if (token) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (
            error?.response?.status === 401 &&
            localStorage.getItem('rfq_access_token')
        ) {
            localStorage.removeItem('rfq_access_token');
            window.location.reload();
        }
        return Promise.reject(error);
    }
);

export const rfqApi = {
    list: async (): Promise<RFQ[]> => {
        const response: AxiosResponse<RFQ[]> = await api.get('/api/rfqs');
        return response.data;
    },

    get: async (id: string): Promise<RFQ> => {
        const response: AxiosResponse<RFQ> = await api.get(`/api/rfqs/${id}`);
        return response.data;
    },

    create: async (rfq: RFQCreate): Promise<RFQ> => {
        const response: AxiosResponse<RFQ> = await api.post('/api/rfqs', rfq);
        return response.data;
    },

    update: async (id: string, rfq: RFQUpdate): Promise<RFQ> => {
        const response: AxiosResponse<RFQ> = await api.put(`/api/rfqs/${id}`, rfq);
        return response.data;
    },

    delete: async (id: string): Promise<void> => {
        await api.delete(`/api/rfqs/${id}`);
    },

    getConveyorStations: async (rfqId: string): Promise<ConveyorStationWithCost[]> => {
        const response = await api.get<ConveyorStationWithCost[]>(
            `/api/rfqs/${rfqId}/conveyor-stations`
        );
        return response.data;
    },

    updateConveyorStations: async (
        rfqId: string,
        stations: ConveyorStationConfig[]
    ): Promise<ConveyorStationWithCost[]> => {
        const response = await api.put<ConveyorStationWithCost[]>(
            `/api/rfqs/${rfqId}/conveyor-stations`,
            { stations }
        );
        return response.data;
    },

    uploadDocument: async (rfqId: string, file: File): Promise<RFQDocumentUploadResponse> => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post<RFQDocumentUploadResponse>(
            `/api/rfqs/${rfqId}/documents`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 120000,
            }
        );
        return response.data;
    },

    getDocuments: async (rfqId: string): Promise<RFQDocumentUploadResponse[]> => {
        const response = await api.get<RFQDocumentUploadResponse[]>(
            `/api/rfqs/${rfqId}/documents`
        );
        return response.data;
    },

    deleteDocument: async (rfqId: string, documentId: string): Promise<void> => {
        await api.delete(`/api/rfqs/${rfqId}/documents/${documentId}`);
    },

    updateDocumentSequence: async (
        rfqId: string,
        documentId: string,
        sequence: string[]
    ): Promise<RFQDocumentUploadResponse> => {
        const response = await api.put<RFQDocumentUploadResponse>(
            `/api/rfqs/${rfqId}/documents/${documentId}/sequence`,
            { sequence_of_operations: sequence }
        );
        return response.data;
    },

    getProgramParams: async (rfqId: string): Promise<RFQProgramParams> => {
        const response = await api.get<RFQProgramParams>(
            `/api/rfqs/${rfqId}/program-params`
        );
        return response.data;
    },

    updateProgramParams: async (
        rfqId: string,
        params: RFQProgramParams
    ): Promise<RFQProgramParams> => {
        const response = await api.put<RFQProgramParams>(
            `/api/rfqs/${rfqId}/program-params`,
            params
        );
        return response.data;
    },
};

export const drawingApi = {
    preview: async (
        rfqId: string,
        file: File
    ): Promise<ExtractedConveyorLayout> => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post<ExtractedConveyorLayout>(
            `/api/rfqs/${rfqId}/drawing/preview`,
            formData,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 120000,
            }
        );
        return response.data;
    },

    apply: async (
        rfqId: string,
        payload: ConveyorDrawingApplyPayload
    ): Promise<ConveyorStationWithCost[]> => {
        const response = await api.post<ConveyorStationWithCost[]>(
            `/api/rfqs/${rfqId}/drawing/apply`,
            payload
        );
        return response.data;
    },
};

export const conveyorStationsApi = {
    listStationTypes: async (): Promise<PredefinedStation[]> => {
        const response = await api.get<PredefinedStation[]>(
            '/api/conveyor-station-types'
        );
        return response.data;
    },
};

export const inventoryApi = {
    listEquipment: async (): Promise<Equipment[]> => {
        const response = await api.get<Equipment[]>('/api/equipment');
        return response.data;
    },

    listLaborRates: async (): Promise<LaborRate[]> => {
        const response = await api.get<LaborRate[]>('/api/labor-rates');
        return response.data;
    },

    updateLaborRates: async (rates: LaborRate[]): Promise<LaborRate[]> => {
        const response = await api.put<LaborRate[]>('/api/labor-rates', { rates });
        return response.data;
    },
};

export const processTemplatesApi = {
    list: async (): Promise<ProcessTemplate[]> => {
        const response = await api.get<ProcessTemplate[]>('/api/process-templates');
        return response.data;
    },

    get: async (id: string): Promise<ProcessTemplate> => {
        const response = await api.get<ProcessTemplate>(`/api/process-templates/${id}`);
        return response.data;
    },

    create: async (payload: ProcessTemplateCreate): Promise<ProcessTemplate> => {
        const response = await api.post<ProcessTemplate>('/api/process-templates', payload);
        return response.data;
    },

    update: async (id: string, payload: ProcessTemplateUpdate): Promise<ProcessTemplate> => {
        const response = await api.put<ProcessTemplate>(
            `/api/process-templates/${id}`,
            payload
        );
        return response.data;
    },

    remove: async (id: string): Promise<void> => {
        await api.delete(`/api/process-templates/${id}`);
    },
};

export const procurementApi = {
    listVendors: async (): Promise<Vendor[]> => {
        const response = await api.get<Vendor[]>('/api/vendors');
        return response.data;
    },

    get: async (rfqId: string): Promise<ProcurementResponse> => {
        const response = await api.get<ProcurementResponse>(
            `/api/rfqs/${rfqId}/procurement`
        );
        return response.data;
    },

    update: async (
        rfqId: string,
        equipmentId: string,
        payload: ProcurementLineUpdate
    ): Promise<ProcurementLine> => {
        const response = await api.put<ProcurementLine>(
            `/api/rfqs/${rfqId}/procurement/${equipmentId}`,
            payload
        );
        return response.data;
    },

    remove: async (rfqId: string, equipmentId: string): Promise<void> => {
        await api.delete(`/api/rfqs/${rfqId}/procurement/${equipmentId}`);
    },
};

export const quoteApi = {
    getTerms: async (): Promise<string> => {
        const response = await api.get<{ markdown: string }>('/api/quote/terms');
        return response.data.markdown;
    },
};

export type ExportDetail = 'itemized' | 'aggregated';

const filenameFromDisposition = (
    disposition: string | undefined,
    fallback: string
): string => {
    if (!disposition) return fallback;
    const match = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
    return match?.[1]?.trim() || fallback;
};

export const exportApi = {
    downloadXlsx: async (rfqId: string, detail: ExportDetail): Promise<void> => {
        const response = await api.get<Blob>(
            `/api/rfqs/${rfqId}/export/xlsx`,
            {
                params: { detail },
                responseType: 'blob',
            }
        );
        const filename = filenameFromDisposition(
            response.headers['content-disposition'],
            `${rfqId}-${detail}.xlsx`
        );
        const url = URL.createObjectURL(response.data);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    },
};

export const authApi = {
    login: async (username: string, password: string): Promise<void> => {
        // Backend expects form-encoded data for OAuth2PasswordRequestForm
        const formData = new URLSearchParams();
        formData.append('username', username);
        formData.append('password', password);

        const response = await api.post<{ access_token: string; token_type: string }>(
            '/api/auth/login',
            formData,
            {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            }
        );

        localStorage.setItem('rfq_access_token', response.data.access_token);
    },

    logout: (): void => {
        localStorage.removeItem('rfq_access_token');
    },
};
